import {AVATAR_PALETTES, paletteForName, type AvatarPalette} from 'ch3-ui-lib';
import type {Call, TranscriptSegment} from '../../../../store/features/calls/callsService';
import {getFormatedPhoneNumber} from '../../../../utilis/msisdnUtilis';
import {getFullName} from '../../../../utilis/getFullName';

export type SpeakerSide = 'client' | 'agent';

export interface Speaker {
    side: SpeakerSide;
    name: string;
    palette: AvatarPalette;
    known: boolean;
    avatarUrl?: string;
}

export interface ResolvedSegment {
    segmentId: number;
    startTime: number;
    endTime: number;
    text: string;
    speaker: Speaker;
}

export interface SpeakerNames {
    client: string;
    agent: string;
}

const CLIENT_CHANNEL = 1;

export const segmentSide = (segment: TranscriptSegment): SpeakerSide => {
    if (segment.channelId !== null && segment.channelId !== undefined && segment.channelId !== '') {
        return Number(segment.channelId) === CLIENT_CHANNEL ? 'client' : 'agent';
    }
    if (segment.clientLabel) return 'client';
    return segment.userId == null && segment.callee == null ? 'client' : 'agent';
};

const formatNumber = (msisdn: number | string | null | undefined): string | null =>
    msisdn ? getFormatedPhoneNumber(msisdn) : null;

export const clientName = (call: Call | undefined, fallback: string): string => {
    if (call?.primaryContact) {
        const name = getFullName(call.primaryContact.firstName, call.primaryContact.lastName).trim();
        if (name) return name;
    }
    return formatNumber(call?.clientMsisdn) ?? fallback;
};

export const clientShortName = (call: Call | undefined, fallback: string): string =>
    call?.primaryContact?.firstName?.trim() || clientName(call, fallback);

export const agentName = (call: Call | undefined, fallback: string): string => {
    if (call?.forwardedTo) return formatNumber(call.forwardedTo) ?? fallback;
    if (call?.user) {
        const name = getFullName(call.user.firstName, call.user.lastName).trim();
        if (name) return name;
    }
    return fallback;
};

const distinctPalettes = (client: string, agent: string, clientKnown: boolean): {client: AvatarPalette; agent: AvatarPalette} => {
    const clientPalette = clientKnown ? paletteForName(client) : 'neutral';
    let agentPalette = paletteForName(agent);
    if (agentPalette === clientPalette) {
        const colourful: AvatarPalette[] = AVATAR_PALETTES.filter((palette) => palette !== 'neutral');
        agentPalette = colourful[(colourful.indexOf(agentPalette) + 1) % colourful.length];
    }
    return {client: clientPalette, agent: agentPalette};
};

export interface SpeakerCast {
    client: Speaker;
    agent: Speaker;
}

export const speakerCast = (call: Call | undefined, fallback: SpeakerNames): SpeakerCast => {
    const client = clientName(call, fallback.client);
    const agent = agentName(call, fallback.agent);
    const clientKnown = Boolean(call?.primaryContact);
    const palettes = distinctPalettes(client, agent, clientKnown);
    return {
        client: {side: 'client', name: client, palette: palettes.client, known: clientKnown},
        agent: {
            side: 'agent',
            name: agent,
            palette: palettes.agent,
            known: !call?.forwardedTo,
            avatarUrl: call?.forwardedTo ? undefined : call?.user?.avatarUrl || undefined,
        },
    };
};

export const resolveSegments = (segments: readonly TranscriptSegment[] | undefined, cast: SpeakerCast): ResolvedSegment[] =>
    [...(segments ?? [])]
        .sort((a, b) => a.startTime - b.startTime || a.segmentId - b.segmentId)
        .map((segment) => {
            const side = segmentSide(segment);
            const base = cast[side];
            const speaker = side === 'client' && segment.clientLabel && segment.clientLabel !== base.name
                ? {...base, name: segment.clientLabel, known: true}
                : base;
            return {
                segmentId: segment.segmentId,
                startTime: Number(segment.startTime) || 0,
                endTime: Math.max(Number(segment.endTime) || 0, Number(segment.startTime) || 0),
                text: (segment.text ?? '').trim(),
                speaker,
            };
        });

export const laneClass: Record<AvatarPalette, string> = {
    violet: 'bg-avatar-violet-fg',
    blue: 'bg-avatar-blue-fg',
    orange: 'bg-avatar-orange-fg',
    green: 'bg-avatar-green-fg',
    pink: 'bg-avatar-pink-fg',
    neutral: 'bg-avatar-neutral-fg',
};

export const laneTextClass: Record<AvatarPalette, string> = {
    violet: 'text-avatar-violet-fg',
    blue: 'text-avatar-blue-fg',
    orange: 'text-avatar-orange-fg',
    green: 'text-avatar-green-fg',
    pink: 'text-avatar-pink-fg',
    neutral: 'text-avatar-neutral-fg',
};
